import { shouldRemoveHiddenLine, removeLines } from '../components/ReusableFunctions';
import { update_project_file_Api } from '../dashboardapi';

export async function applyAcceptToEditor(editor, item, setTextFile, setAllFiles = null, props) {
    const session = editor.session;
    const Range = window.ace.require('ace/range').Range;
    if (item.styleInfo?.removeType === 'fullLine') {
        const row = item.startAnchor.row;
        const lineCount = session.getLength();

        if (row < lineCount - 1) {
            // Remove entire line including the trailing \n
            // Range from col 0 of this row to col 0 of next row = entire line + \n
            const range = new Range(row, 0, row + 1, 0);
            session.remove(range);
        } else {
            // Last line — just clear content, no \n to remove
            const range = new Range(row, 0, row, session.getLine(row).length);
            session.remove(range);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    if (item.styleInfo?.removeType === 'partial') {
        // Remove only the % comment part — generic fallback handles this fine
        // since suggestionText is '' and the range covers only the comment
        const range = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.remove(range);
        if (setTextFile) setTextFile(session.getValue());
        return;
    }
    if (item.styleInfo?.isBibitemKeyChange) {
        const { oldKey, newKey, allfiles, currentFileUuid } = item.styleInfo;

        // ── Step 1: Replace \bibitem{oldKey} in current editor ───────────────
        const bibRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.replace(bibRange, item.suggestionText);

        // ── Step 2: Also update \cite{oldKey} in the SAME file ───────────────
        // After accepting \bibitem, the \cite anchor for this key is now stale
        // (it still references the old key). Fix it directly in the session.
        const escapedOld = oldKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const citeRegex = new RegExp(
            `(~?\\\\cite\\{[^}]*?)\\b${escapedOld}\\b([^}]*?\\})`,
            'g'
        );

        const currentContent = session.getValue();
        const citeMatches = [];
        let citeMatch;

        // Collect all \cite matches in current file containing oldKey
        const tempRegex = new RegExp(citeRegex.source, 'g');
        while ((citeMatch = tempRegex.exec(currentContent)) !== null) {
            citeMatches.push({
                index: citeMatch.index,
                length: citeMatch[0].length,
                replacement: citeMatch[0].replace(
                    new RegExp(`\\b${escapedOld}\\b`, 'g'),
                    newKey
                )
            });
        }

        // Apply bottom-to-top so indices don't shift
        for (let i = citeMatches.length - 1; i >= 0; i--) {
            const cm = citeMatches[i];
            const startPos = session.doc.indexToPosition(cm.index);
            const endPos = session.doc.indexToPosition(cm.index + cm.length);
            const cRange = new Range(startPos.row, startPos.column, endPos.row, endPos.column);
            session.replace(cRange, cm.replacement);
        }

        if (setTextFile) setTextFile(session.getValue());

        // ── Step 3: Cross-file \cite update ──────────────────────────────────
        if (!Array.isArray(allfiles)) {
            console.warn('[CitationStyling] allfiles is not an array:', typeof allfiles);
            return;
        }

        const updatedFiles = [];

        for (const fileObj of allfiles) {
            if (!fileObj.content) continue;
            if (!fileObj.name?.endsWith('.tex')) continue;
            if (fileObj.file_uuid === currentFileUuid) continue;

            // Normalise line endings only for comparison
            const normalised = fileObj.content.replace(/\r\n/g, '\n');
            citeRegex.lastIndex = 0;
            const updatedNormalised = normalised.replace(
                citeRegex,
                (_, before, after) => `${before}${newKey}${after}`
            );

            if (updatedNormalised === normalised) {
                console.log(`[CitationStyling] no \\cite{${oldKey}} in ${fileObj.name} — skipped`);
                continue;
            }

            // Preserve original \r\n for the saved content
            citeRegex.lastIndex = 0;
            const updatedContent = fileObj.content.replace(
                citeRegex,
                (_, before, after) => `${before}${newKey}${after}`
            );

            try {
                console.log(`[CitationStyling] saving ${fileObj.name}...`);
                await update_project_file_Api(
                    fileObj.project_uuid,
                    fileObj.file_uuid,
                    fileObj.name,
                    updatedContent
                );
                console.log(`✅ Saved ${fileObj.name}: \\cite{${oldKey}} → \\cite{${newKey}}`);
                updatedFiles.push({ ...fileObj, content: updatedContent });
            } catch (err) {
                console.error(`❌ Failed to save ${fileObj.name}:`, err);
            }
        }

        // ── Step 4: Refresh Allfiles state ────────────────────────────────────
        if (updatedFiles.length > 0 && typeof setAllFiles === 'function') {
            setAllFiles(prev => {
                // Handle both shapes: plain array OR { data: [], error: null }
                const prevData = Array.isArray(prev) ? prev : (prev?.data || []);
                const newData = prevData.map(f => {
                    const updated = updatedFiles.find(u => u.file_uuid === f.file_uuid);
                    return updated ?? f;
                });
                return Array.isArray(prev) ? newData : { ...prev, data: newData };
            });
            console.log(`[CitationStyling] Allfiles state refreshed for ${updatedFiles.length} file(s)`);
        } else if (updatedFiles.length > 0 && !setAllFiles) {
            console.warn('[CitationStyling] setAllFiles not provided — in-memory Allfiles is stale');
        }

        return;
    }
    if (item.styleInfo?.isLabelFix) {
        const { oldKey, newKey } = item.styleInfo;

        // 1. Replace \label{oldKey} → \label{newKey}
        session.replace(
            new Range(item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column),
            item.suggestionText
        );

        // 2. Rename oldKey → newKey in every \ref/\eqref/\cref/\autoref/\pageref
        const escaped = oldKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const refPat = new RegExp(
            `(\\\\(?:eq|auto|page|[cC])?ref\\{[^}]*?)\\b${escaped}\\b([^}]*?\\})`, 'g'
        );
        const content = session.getValue();
        const refMatches = [];
        let rm;
        while ((rm = refPat.exec(content)) !== null) {
            refMatches.push({
                index: rm.index, length: rm[0].length,
                replacement: rm[1] + newKey + rm[2],
            });
        }
        // Apply bottom-to-top so indices stay valid
        for (let i = refMatches.length - 1; i >= 0; i--) {
            const { index, length, replacement } = refMatches[i];
            const s = session.doc.indexToPosition(index);
            const e = session.doc.indexToPosition(index + length);
            session.replace(new Range(s.row, s.column, e.row, e.column), replacement);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    // ── isLabelAdd: no label on \begin{...} → insert on next line ─────────────
    // suggestionText already = \begin{...}\n\label{newKey}
    if (item.styleInfo?.isLabelAdd) {
        session.replace(
            new Range(item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column),
            item.suggestionText
        );
        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    // ── isLabelAddRow: no label on row → insert \label before \\ or \end{...} ─
    // anchor    = \\ or \end{alignat} etc.
    // suggestion = \label{newKey} \\   or   \label{newKey} \end{alignat}
    if (item.styleInfo?.isLabelAddRow) {
        session.replace(
            new Range(item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column),
            item.suggestionText
        );
        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    // ── isLabelAdd: missing label on \begin{...} ──────────────────────────────
    // suggestionText already = \begin{...}\n\label{newKey}
    if (item.styleInfo?.isLabelAdd) {
        session.replace(
            new Range(item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column),
            item.suggestionText
        );
        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    // ── isLabelAddRow: missing label on a subequation row ─────────────────────
    // anchor = \\  or  \end{subequations}
    // suggestionText = \label{newKey} \\  or  \label{newKey} \end{subequations}
    if (item.styleInfo?.isLabelAddRow) {
        session.replace(
            new Range(item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column),
            item.suggestionText
        );
        if (setTextFile) setTextFile(session.getValue());
        return;
    }


    if (item.styleInfo?.isPreambleInsertion) {
        const { textAboveTitle } = item.styleInfo;
        const currentContent = session.getValue();

        // Find \title again in current content
        const titleMatch = /\\title\s*\{/i.exec(currentContent);
        if (!titleMatch) return;

        const titlePos = session.doc.indexToPosition(titleMatch.index);

        // Replace everything from (0,0) to \title position with preamble
        const range = new Range(0, 0, titlePos.row, titlePos.column);
        session.replace(range, item.suggestionText);

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    if (item.styleInfo?.isListLevelInsertion) {
        const currentContent = session.getValue();
        // 1. Find the current index of \begin{document}
        const docMatch = /\\begin\{document\}/i.exec(currentContent);
        if (!docMatch) return;

        // 2. Convert that index to a Row/Column position
        const docPos = session.doc.indexToPosition(docMatch.index);

        // 3. Define the insertion point (Row of the match, Column 0) 
        // to ensure it goes on the line ABOVE the command.
        const insertPos = { row: docPos.row, column: 0 };

        // 4. Insert the suggestion text + a newline to push the command down
        session.insert(insertPos, item.suggestionText);

        if (setTextFile) setTextFile(session.getValue());
        return;
    }
    if (item.extraStyleInfo?.isCaptionMove) {
        const captionText = item.extraStyleInfo.captionText;

        // ── STEP 1: Remove the \caption{...} from its original position ──────────
        const session = editor.session;
        const captionRow = item.startAnchor.row;
        const captionCol = item.startAnchor.column;
        const captionLine = session.getLine(captionRow);

        // Remove the caption text from the line
        const lineAfterRemoval = captionLine.slice(0, captionCol)
            + captionLine.slice(captionCol + captionText.length);

        // Check if the line is now empty or contains only lone braces/whitespace.
        // e.g. "{\caption{...}" → after removal → "{"  ← should be removed
        const cleanedLine = lineAfterRemoval.trim();
        const isLoneOrEmpty = cleanedLine === '' || /^[{}\s]*$/.test(cleanedLine);

        if (isLoneOrEmpty) {
            // Remove the entire line (and its trailing newline)
            const lineCount = session.getLength();
            if (captionRow < lineCount - 1) {
                // Delete from start of this line to start of next line
                session.remove({
                    start: { row: captionRow, column: 0 },
                    end: { row: captionRow + 1, column: 0 },
                });
            } else {
                // Last line: just clear the content
                session.replace(
                    {
                        start: { row: captionRow, column: 0 },
                        end: { row: captionRow, column: captionLine.length }
                    },
                    ''
                );
            }
        } else {
            // Line still has useful content — replace only the caption portion
            session.replace(
                {
                    start: { row: captionRow, column: captionCol },
                    end: { row: captionRow, column: captionCol + captionText.length }
                },
                ''
            );
        }

        // ── STEP 2: Find \includegraphics in the updated session text ─────────────
        // Search from the current caption row onwards (caption was removed, so
        // \includegraphics has now shifted up or is on the same/nearby row).
        const updatedLines = session.getLines(0, session.getLength() - 1);
        let incRow = -1;

        // Search near the original caption row (±20 lines is generous)
        const searchStart = Math.max(0, captionRow - 2);
        const searchEnd = Math.min(updatedLines.length - 1, captionRow + 20);

        for (let r = searchStart; r <= searchEnd; r++) {
            if (/\\includegraphics/.test(updatedLines[r])) {
                incRow = r;
                break;
            }
        }

        if (incRow === -1) {
            // Fallback: search the whole document
            for (let r = 0; r < updatedLines.length; r++) {
                if (/\\includegraphics/.test(updatedLines[r])) {
                    incRow = r;
                    break;
                }
            }
        }

        if (incRow === -1) {
            console.warn('[captionMove] Could not find \\includegraphics after caption removal');
            return;
        }

        // ── STEP 3: Insert caption as a NEW LINE below the \includegraphics row ───
        //
        // OLD (buggy): inserted at the brace-group end of \includegraphics{...}
        //   → ended up INSIDE \TRSFIG{} when command was \TRSFIG{\includegraphics{...}}
        //
        // NEW (fixed): insert at column 0 of the NEXT row
        //   → always places caption as its own line, completely outside any wrapper
        //
        // Examples:
        //   \includegraphics{file}      → caption on next line  ✓
        //   \TRSFIG{\includegraphics{}} → caption on next line  ✓  (not inside TRSFIG)
        session.insert(
            { row: incRow + 1, column: 0 },
            captionText + '\n'
        );

        // ── STEP 4: Update state ───────────────────────────────────────────────────
        if (setTextFile) setTextFile(session.getValue());
        // Clean up the anchor (standard post-accept cleanup)
        cleanupItem(editor.session, item, props);
        return;
    }

    if (item.styleInfo?.authorAction === 'moveAfterShorttitle') {
        const shorttitleText = item.styleInfo.shorttitleText;
        const fixedAuthor = item.suggestionText;
        const contentBefore = session.getValue();
        const shorttitleIndex = contentBefore.indexOf(shorttitleText);
        const shorttitleEnd = shorttitleIndex + shorttitleText.length;
        const deleteRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        const textBetween = contentBefore.slice(shorttitleEnd, contentBefore.indexOf(item.originalText));
        const isAlreadyAfter = /^\s*$/.test(textBetween);

        if (isAlreadyAfter) {
            session.replace(deleteRange, fixedAuthor);
        } else {
            session.remove(deleteRange);
            const contentAfterDelete = session.getValue();
            const newShorttitleIndex = contentAfterDelete.indexOf(shorttitleText);
            if (newShorttitleIndex === -1) {
                session.insert({ row: 0, column: 0 }, fixedAuthor + '\n\n');
            } else {
                const insertAfterIndex = newShorttitleIndex + shorttitleText.length;
                const insertPos = session.doc.indexToPosition(insertAfterIndex);
                session.insert(insertPos, `\n\n${fixedAuthor}`);
            }
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    if (item.styleInfo?.name === "Proper Label and Ref") {
        const oldLabel = item.styleInfo.oldLabelName;
        const newLabel = item.styleInfo.newLabelName;
        const labelRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.replace(labelRange, item.suggestionText);

        if (oldLabel && newLabel) {
            const escapedOld = oldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const refPattern = new RegExp(
                `\\\\(?:ref|eqref|autoref|cref|Cref|pageref)\\s*\\{${escapedOld}\\}`,
                'g'
            );
            const currentContent = session.getValue();
            let match;
            const refMatches = [];
            while ((match = refPattern.exec(currentContent)) !== null) {
                refMatches.push({
                    index: match.index,
                    length: match[0].length,
                    fullMatch: match[0],
                    replacement: match[0].replace(oldLabel, newLabel)
                });
            }
            for (let i = refMatches.length - 1; i >= 0; i--) {
                const ref = refMatches[i];
                const startPos = session.doc.indexToPosition(ref.index);
                const endPos = session.doc.indexToPosition(ref.index + ref.length);
                const refRange = new Range(startPos.row, startPos.column, endPos.row, endPos.column);
                session.replace(refRange, ref.replacement);
            }
            console.log(`Renamed \\label{${oldLabel}} → \\label{${newLabel}}, updated ${refMatches.length} refs`);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }
    // if (item.styleInfo?.isCaptionMove) {
    //     const { captionText } = item.styleInfo;
    //     const currentContent = session.getValue();

    //     const captionIdx = currentContent.indexOf(captionText);
    //     if (captionIdx === -1) {
    //         // Caption no longer found — nothing to do
    //         if (setTextFile) setTextFile(currentContent);
    //         return;
    //     }

    //     let afterRemoval;
    //     const afterCaption = currentContent[captionIdx + captionText.length];
    //     if (afterCaption === '\n') {
    //         // Remove caption + trailing newline
    //         afterRemoval = currentContent.slice(0, captionIdx)
    //             + currentContent.slice(captionIdx + captionText.length + 1);
    //     } else {
    //         // Remove caption only
    //         afterRemoval = currentContent.slice(0, captionIdx)
    //             + currentContent.slice(captionIdx + captionText.length);
    //     }
    //     const searchFrom = captionIdx; // after removal, this is where the image should be

    //     const incPat = /\\includegraphics\s*(?:\[[^\]]*\])?\s*\{[^}]*\}/g;
    //     incPat.lastIndex = searchFrom;

    //     let bestInc = null;
    //     let incMatch = incPat.exec(afterRemoval);
    //     if (!incMatch) {
    //         // Fallback: search from beginning if not found after captionIdx
    //         incPat.lastIndex = 0;
    //         incMatch = incPat.exec(afterRemoval);
    //     }
    //     bestInc = incMatch;

    //     if (!bestInc) {
    //         // No \includegraphics found — just apply the removal
    //         const startPos = session.doc.indexToPosition(captionIdx);
    //         const removeLen = captionText.length + (afterCaption === '\n' ? 1 : 0);
    //         const endPos = session.doc.indexToPosition(captionIdx + removeLen);
    //         session.replace(new Range(startPos.row, startPos.column, endPos.row, endPos.column), '');
    //         if (setTextFile) setTextFile(session.getValue());
    //         return;
    //     }

    //     const incEndIdx = bestInc.index + bestInc[0].length;
    //     // Insert caption on a new line immediately after the \includegraphics token
    //     const finalContent = afterRemoval.slice(0, incEndIdx)
    //         + '\n' + captionText
    //         + afterRemoval.slice(incEndIdx);


    //     const AceRange = window.ace.require('ace/range').Range;
    //     const lastRow = session.getLength() - 1;
    //     const lastCol = session.getLine(lastRow).length;
    //     const fullRange = new AceRange(0, 0, lastRow, lastCol);
    //     session.replace(fullRange, finalContent);

    //     if (setTextFile) setTextFile(session.getValue());
    //     return;
    // }
    if (item.styleInfo?.removeType === 'tableUnitGroup') {

        const headerRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.replace(headerRange, item.suggestionText);
        const dataRows = item.styleInfo.dataRows.slice().reverse();
        for (const d of dataRows) {
            const currentText = session.getValue();
            const cellIdx = currentText.indexOf(d.original, Math.max(0, d.absIndex - 10));
            if (cellIdx === -1) continue;

            const startPos = session.doc.indexToPosition(cellIdx);
            const endPos = session.doc.indexToPosition(cellIdx + d.original.length);
            session.replace(new Range(startPos.row, startPos.column, endPos.row, endPos.column), d.replacement);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }



    // Inside applyAcceptToEditor...

    if (item.styleInfo?.maketitleAction === 'moveAndClean') {
        // --- STEP 1: REMOVE ALL EXISTING \maketitle ---
        let currentContent = session.getValue();
        const maketitleRegex = /\\maketitle/g;
        let match;
        const matchesToRemove = [];

        // Find all occurrences first to avoid index shifting issues during loop
        while ((match = maketitleRegex.exec(currentContent)) !== null) {
            matchesToRemove.push({
                index: match.index,
                length: match[0].length
            });
        }

        // Remove from bottom to top to preserve indices
        for (let i = matchesToRemove.length - 1; i >= 0; i--) {
            const m = matchesToRemove[i];
            const startPos = session.doc.indexToPosition(m.index);
            const endPos = session.doc.indexToPosition(m.index + m.length);
            const rangeToRemove = new Range(startPos.row, startPos.column, endPos.row, endPos.column);
            session.remove(rangeToRemove);
        }

        // --- STEP 2: INSERT THE NEW ONE AT \begin{document} ---
        // We refresh the content because the row/column of the anchor might have shifted 
        // if the deleted \maketitle was above the \begin{document}
        const refreshedContent = session.getValue();
        const docMatch = /\\begin\{document\}/.exec(refreshedContent);

        if (docMatch) {
            const startPos = session.doc.indexToPosition(docMatch.index);
            const endPos = session.doc.indexToPosition(docMatch.index + docMatch[0].length);
            const finalDocRange = new Range(startPos.row, startPos.column, endPos.row, endPos.column);

            session.replace(finalDocRange, item.suggestionText);
        } else {
            // Fallback: Use the original anchor if the text search fails
            const fallbackRange = new Range(
                item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column
            );
            session.replace(fallbackRange, item.suggestionText);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }
    if (item.styleInfo?.removeType === 'affiliationRestructure') {
        // The suggestion covers the entire author+address block.
        // Use a text-search replace so it still works even if earlier
        // edits shifted the anchor's row/col slightly.
        const currentContent = session.getValue();
        const foundIdx = currentContent.indexOf(item.originalText);

        if (foundIdx !== -1) {
            // Found the original block in the current document → replace it
            const startPos = session.doc.indexToPosition(foundIdx);
            const endPos = session.doc.indexToPosition(foundIdx + item.originalText.length);
            session.replace(
                new Range(startPos.row, startPos.column, endPos.row, endPos.column),
                item.suggestionText
            );
        } else {
            // Fallback: trust the live anchors
            session.replace(
                new Range(
                    item.startAnchor.row, item.startAnchor.column,
                    item.endAnchor.row, item.endAnchor.column
                ),
                item.suggestionText
            );
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }


    // ── Generic fallback replace ──────────────────────────────────────────────
    const range = new Range(
        item.startAnchor.row, item.startAnchor.column,
        item.endAnchor.row, item.endAnchor.column
    );
    session.replace(range, item.suggestionText);
    if (setTextFile) setTextFile(session.getValue());
}



export function cleanupItem(session, item, props) {
    if (item.markerId) session?.removeMarker(item.markerId);
    if (item.startAnchor?.detach) item.startAnchor.detach();
    if (item.endAnchor?.detach) item.endAnchor.detach();
}

export function removeFromAnchorList(globalAnchorList, item) {
    const index = globalAnchorList.indexOf(item);
    if (index > -1) globalAnchorList.splice(index, 1);
}